import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Spin,
  message,
} from "antd";
import React, { useEffect, useState, useRef } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import SimpleReactValidator from "simple-react-validator";
import CompanyServices from "../../services/CompanyServices";
import { translate } from "../../locale";
import TextArea from "antd/lib/input/TextArea";
import { useNavigate } from "react-router-dom";
import PickVademecum from "../PickVademecum";

const EditCompany = ({ visible, onComplete, onCancel, enterpriseId }) => {
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(false);
  const [values, setValues] = useState({
    name: "",
    terms: "",
    vademecum: [],
  });
  const [error, setError] = useState("");
  const [, forceUpdate] = useState();
  const navigate = useNavigate();

  const simpleValidator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
      element: (message) => (
        <div className="text-red-600 pt-2 text-xs">{message}</div>
      ),
      messages: {
        required: "Campo requerido",
      },
    })
  );

  useEffect(() => {
    if (visible !== null) {
      setActive(visible);
    }
    loadCompanies();
  }, [visible]);

  const disabled = () => !simpleValidator.current.allValid();

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const res = await CompanyServices.getCompany(enterpriseId);

      if (res.status >= 200 && res.status < 300) {
        setValues({
          name: res.data.enterprises.name,
          terms: res.data.enterprises.terms_conditions,
          vademecum: res.data.enterprises.vademecums.map((v) => v.id),
        });
      } else if (res.status === 400) {
        message.error(translate(res.data.message));
        navigate("/admin/companies");
        return;
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const onFinish = async () => {
    setLoading(true);
    setError("");
    if (!simpleValidator.current.allValid()) {
      simpleValidator.current.showMessages();
      forceUpdate();
      setError("Todos los datos son requeridos");
      return;
    }
    try {
      const data = {
        id: enterpriseId,
        name: values.name,
        terms_conditions: values.terms,
        vademecums: values.vademecum,
      };
      const res = await CompanyServices.updateCompany(data);
      if (res) {
        if (res.status >= 200 && res.status < 300) {
          message.success(
            translate("La {0} se editó exitosamente.", translate("empresa"))
          );
        } else if (res.data.detail) {
          message.error(res.data.detail, 6);
        } else {
          message.success(res.data.message, 6);
        }
        resetForm();
        setLoading(false);
        if (onComplete) {
          onComplete();
        }
      } else {
        setError("Edit company error");
      }
    } catch (e) {
      console.log(e);
      setError(e.message);
      setLoading(false);
    }
  };

  const resetForm = () => {
    setValues({
      name: "",
    });
    simpleValidator.current.hideMessages();
    simpleValidator.current.visibleFields = [];
    forceUpdate();
  };

  const handleChange = (name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    simpleValidator.current.showMessageFor(name);
    setValues((v) => ({ ...v, [name]: value }));
  };

  const handleCancel = () => {
    resetForm();
    setActive(false);
    if (onCancel) {
      onCancel();
    }
  };

  const handleAddVademecum = (id) => {
    setValues((prevValues) => {
      const isSelected = prevValues?.vademecum?.includes(id);
      const newVademecum = isSelected
        ? prevValues?.vademecum?.filter((vid) => vid !== id)
        : [...prevValues.vademecum, id];

      return { ...prevValues, vademecum: newVademecum };
    });
  };

  return (
    <Modal
      open={active}
      title={translate("Editar Empresa")}
      onOk={null}
      onCancel={handleCancel}
      footer={false}
    >
      {error && (
        <Alert
          message={error}
          type="error"
          className="mb-6"
          closable
          onClose={() => setError("")}
        />
      )}
      <Form
        initialValues={{ remember: true }}
        onFinish={onFinish}
        autoComplete="off"
        className="w-full"
      >
        <Form.Item>
          <Input
            placeholder="Nombre"
            className="border-custom_primary"
            name="name"
            onChange={(e) => handleChange("name", e.target.value)}
            onBlur={handleBlur}
            value={values.name}
          />
          {simpleValidator.current.message("name", values.name, "required")}
        </Form.Item>

        <Form.Item>
          <TextArea
            placeholder="Términos y condiciones"
            className="border-custom_primary"
            name="terms"
            onChange={(e) => handleChange("terms", e.target.value)}
            onBlur={handleBlur}
            value={values.terms}
          />
          {simpleValidator.current.message("terms", values.terms, "required")}
        </Form.Item>
        <Form.Item>
          <PickVademecum
            onClickVademecum={(id) => handleAddVademecum(id)}
            values={values}
          />
        </Form.Item>
        <Form.Item>
          <Row align="end" gutter={16}>
            <Col>
              <Button key="back" onClick={handleCancel}>
                {translate("Cancelar")}
              </Button>
            </Col>
            <Col>
              <Button
                disabled={disabled()}
                htmlType="submit"
                type="primary"
                className="bg-custom_primary"
              >
                {loading ? (
                  <Spin
                    indicator={<LoadingOutlined style={{ color: "#666666" }} />}
                  />
                ) : (
                  "Editar"
                )}
              </Button>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditCompany;
